import styled from '@emotion/styled';
import { SessionState } from '@heygen/liveavatar-web-sdk';
import type { TTSConfig } from '@trinity/chat/types/tts-config';
import { Modal, Header } from '../Modal';
import { AvatarPreviewBridge } from './AvatarPreviewBridge';
import { LiveAvatar } from './LiveAvatar';
import { useLiveAvatarContextOptional } from './LiveAvatar.context';
import { LiveAvatarSessionManager } from './LiveAvatarSessionManager';

const DEFAULT_SAMPLE_TEXT =
  'Hello! I am your segment representative, and I am ready to answer your questions.';


const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 8px 0 16px;
`;

const VideoWrapper = styled.div`
  position: relative;
  width: 100%;
  /* Maintain a 9:16-ish portrait aspect ratio typical for HeyGen avatars */
  aspect-ratio: 9 / 16;
  max-height: 480px;
  background-color: #111;
  border-radius: 8px;
  overflow: hidden;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.02em;
  pointer-events: none;
`;

const HintText = styled.p`
  margin: 0;
  font-size: 13px;
  color: #888;
  text-align: center;
`;


type InnerProps = {
  sampleText: string;
  ttsConfig: TTSConfig | null;
};

function AvatarPreviewContent({ sampleText, ttsConfig }: InnerProps): React.ReactElement {
  const ctx = useLiveAvatarContextOptional();

  const isConnected = ctx?.sessionState === SessionState.CONNECTED;
  const isStreamReady = ctx?.isStreamReady ?? false;
  const showLoading = !isStreamReady;

  return (
    <Content>
      <VideoWrapper>
        <LiveAvatar />
        {showLoading && (
          <LoadingOverlay>
            {isConnected ? 'Preparing avatar…' : 'Connecting…'}
          </LoadingOverlay>
        )}
      </VideoWrapper>
      {isStreamReady && (
        <HintText>The avatar will speak a sample greeting.</HintText>
      )}
      {/* Fires the TTS call once isStreamReady becomes true */}
      <AvatarPreviewBridge sampleText={sampleText} ttsConfig={ttsConfig} />
    </Content>
  );
}


type Props = {
  /**
   * Whether the modal is open. When changed from true → false the
   * LiveAvatarSessionManager unmounts, which stops the HeyGen session.
   */
  open: boolean;
  onClose: () => void;
  /**
   * HeyGen avatar ID as stored in segment.ttsSettings.avatarId.
   * Required — the modal renders nothing when falsy.
   */
  avatarId: string | undefined;
  /**
   * TTS provider config saved on the segment.
   * Passed through to AvatarPreviewBridge for synthesis.
   */
  ttsConfig: TTSConfig | null;
  /**
   * Optional override for the sample text spoken by the avatar.
   * Defaults to a generic greeting.
   */
  sampleText?: string;
};

/**
 * AvatarPreviewModal
 *
 * Opens a modal that starts a HeyGen LITE session for the given `avatarId`
 * and has the avatar speak a sample sentence using the segment's TTS config.
 *
 * Session lifecycle:
 *   - Session starts when `open` becomes true (LiveAvatarSessionManager mounts).
 *   - Session stops when `open` becomes false (LiveAvatarSessionManager unmounts
 *     → LiveAvatarContextProvider cleanup calls session.stop()).
 *
 * Must be rendered inside a ChatApiProvider tree so that useChatApiClient()
 * works inside LiveAvatarSessionManager and AvatarPreviewBridge.
 * In apps/frontend this is satisfied by ChatCompatabilityProvider in MainSubscriber.
 */
export function AvatarPreviewModal({
  open,
  onClose,
  avatarId,
  ttsConfig,
  sampleText = DEFAULT_SAMPLE_TEXT,
}: Props): React.ReactElement | null {
  // If no avatar is configured yet we have nothing to preview.
  if (!avatarId) {
    return null;
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Header>Avatar Preview</Header>
      {/*
       * LiveAvatarSessionManager is mounted conditionally via the Modal's
       * open prop — the Modal renders null when closed, so the session is
       * automatically stopped when the user closes the modal.
       */}
      <LiveAvatarSessionManager
        avatarId={avatarId}
        ttsConfig={ttsConfig}>
        <AvatarPreviewContent sampleText={sampleText} ttsConfig={ttsConfig} />
      </LiveAvatarSessionManager>
    </Modal>
  );
}
